import Markdown from '@/components/markdown/markdown';
import { useEnterKeyGlobal } from '@/hooks/use-enter-key';
import { Box, Button, Flex, IconArrowRight, Image, Text } from '@cmpsr/components';
import { FC } from 'react';
import { DECISION_TREE_CTA_TEST_ID } from '../../../../constants';
import { useImageInterstitialController } from './ImageInterstitial.controller';
import { ImageInterstitialProps } from './types';

export const ImageInterstitial: FC<ImageInterstitialProps> = ({ data, saveAnswer, goToNextSurveyItem }) => {
  const { title, text, imageId, isLoading, handleNextClick, buttonLabel } = useImageInterstitialController({
    saveAnswer,
    data,
    goToNextSurveyItem,
  });

  useEnterKeyGlobal(handleNextClick);

  return (
    <Flex
      textAlign="center"
      pt={{ lg: '16' }}
      alignSelf="stretch"
      gap={{ base: '6', md: '16' }}
      alignItems="center"
      justifyContent={{ lg: 'center' }}
      flexDirection={{ base: 'column', lg: 'row' }}
      flex="1"
      id="image-interstitial"
      minHeight="80vh"
    >
      <Flex alignItems="center" justifyContent="center">
        {/* Rendered only on lg screens and above */}
        <Box display={{ base: 'none', lg: 'block' }} width="350px" height="350px" borderRadius="full" overflow="hidden">
          <Image
            srcSet={`/images/interstitials/${imageId}/${imageId}-400w.avif 400w, /images/interstitials/${imageId}/${imageId}-600w.avif 600w, /images/interstitials/${imageId}/${imageId}-800w.avif 800w, /images/interstitials/${imageId}/${imageId}-1000w.avif 1000w, /images/interstitials/${imageId}/${imageId}-1200w.avif 1200w, /images/interstitials/${imageId}/${imageId}-1600w.avif 1600w, /images/interstitials/${imageId}/${imageId}-2000w.avif 2000w`}
            sizes="(max-width: 400px) 400px, (max-width: 600px) 600px, (max-width: 800px) 800px, (max-width: 1000px) 1000px, (max-width: 1200px) 1200px, (max-width: 1600px) 1600px, (min-width: 1601px) 2000px"
            src={`/images/interstitials/${imageId}/${imageId}-400w.avif`}
            alt={title}
            width="100%"
            height="100%"
            objectFit="cover"
            objectPosition="center"
          />
        </Box>

        {/* Rendered only below lg screens */}
        <Image
          display={{ base: 'block', lg: 'none' }}
          srcSet={`/images/interstitials/${imageId}/${imageId}-400w.avif 400w, /images/interstitials/${imageId}/${imageId}-600w.avif 600w, /images/interstitials/${imageId}/${imageId}-800w.avif 800w, /images/interstitials/${imageId}/${imageId}-1000w.avif 1000w, /images/interstitials/${imageId}/${imageId}-1200w.avif 1200w, /images/interstitials/${imageId}/${imageId}-1600w.avif 1600w, /images/interstitials/${imageId}/${imageId}-2000w.avif 2000w`}
          sizes="(max-width: 400px) 400px, (max-width: 600px) 600px, (max-width: 800px) 800px, (max-width: 1000px) 1000px, (max-width: 1200px) 1200px, (max-width: 1600px) 1600px, (min-width: 1601px) 2000px"
          src={`/images/interstitials/${imageId}/${imageId}-400w.avif`}
          alt={title}
          w="100vw"
          maxH={{ base: '170px', md: '275px' }}
          overflow="hidden"
          objectFit="cover"
          objectPosition="center"
        />
      </Flex>
      <Flex
        textAlign="left"
        maxW={{ base: '450px', md: '600px' }}
        gap="4"
        flexDirection="column"
        alignItems="center"
        px={{ base: '12', md: '20', lg: '22', xl: '24', xxl: '32' }}
      >
        <Text
          variant="text-body-regular"
          textTransform="uppercase"
          fontFamily="Inter"
          color="text-link-secondary-default"
        >
          Why we ask
        </Text>
        {title && (
          <Text variant="text-header-XL">
            <Markdown
              components={{
                p: ({ node, ...props }) => <Text variant="text-header-XL" {...props} />,
              }}
            >
              {title}
            </Markdown>
          </Text>
        )}
        <Box>
          <Markdown
            components={{
              p: ({ node, ...props }) => <Text variant="text-body-meta-regular" color="text-secondary" {...props} />,
              br: () => <Box py="spacer-1" />,
            }}
          >
            {text}
          </Markdown>
        </Box>
        <Flex justifyContent="center">
          <Button
            onClick={handleNextClick}
            variant="accent"
            size={{ base: 's', md: 'm' }}
            trailingIcon={<IconArrowRight />}
            isDisabled={isLoading}
            isLoading={isLoading}
            borderRadius="radii-button"
            px="7 !important"
            py="3 !important"
            mt="6"
            data-testid={DECISION_TREE_CTA_TEST_ID}
          >
            {buttonLabel}
          </Button>
        </Flex>

        <Box position="absolute" bgRepeat="no-repeat" bgPosition="center 100%" bgSize="auto 200px"></Box>
      </Flex>
    </Flex>
  );
};
